import React from 'react';
import { useSearchParams } from 'react-router-dom';

import useOutsideClick from '@hero/ui-kit/hooks/useOutsideClick';
import PlusIcon from '@hero/ui-kit/icons/utility/PlusIcon';
import XIcon from '@hero/ui-kit/icons/utility/XIcon';
import Button from '@hero/ui-kit/inputs/Button';

import useLocalStorage from '../../hooks/useLocalStorage';
import useOrgStorageKey from '../../hooks/useOrgStorageKey';
import * as Style from './style.module.scss';

type SavedFilter = {
    id: string;
    name: string;
    // URL query string (without the leading "?") captured when saved.
    query: string;
};

// Pagination is per-session noise — not part of a saved view.
const PAGINATION_KEYS = ['limit', 'offset'];

// Normalize a query for comparison: drop pagination and sort keys so order and
// page position don't affect whether a saved view matches the current URL.
const canonicalQuery = (query: string) => {
    const params = new URLSearchParams(query);
    PAGINATION_KEYS.forEach((key) => params.delete(key));
    params.sort();
    return params.toString();
};

// Lets the user name and persist the current URL query (filters + search) to
// localStorage for quick re-access. Saved views render as tabs that sit inline
// with a view's preset tabs; the "+ Save current filter" action opens a small
// popover to name a new one. Reusable across pages/lists — pass a unique
// `storageKey` to scope the set (it is further namespaced by the active org so
// saved views are per-organization). `showLeadingDivider` draws a separator
// before the saved tabs when they follow a view's preset tabs.
const SavedFilters: React.FC<{
    storageKey: string;
    ignoreParams?: string[];
    showLeadingDivider?: boolean;
}> = ({ storageKey, ignoreParams = [], showLeadingDivider = false }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    // Saved views are per-organization: a user switching orgs sees only that
    // org's own saved filters. The key is namespaced by the active org id.
    const orgStorageKey = useOrgStorageKey();
    const [saved, setSaved] = useLocalStorage<SavedFilter[]>(orgStorageKey(storageKey), []);
    const [isOpen, setIsOpen] = React.useState(false);
    const [name, setName] = React.useState('');
    const [ref, isOutsideClick] = useOutsideClick(isOpen);

    // Only offer to save when a filter or search term is actually applied.
    // Pagination and the page's tab param (passed via `ignoreParams`) don't count.
    const hasSavableQuery = React.useMemo(() => {
        const nonSaveable = [...PAGINATION_KEYS, ...ignoreParams];
        return Array.from(searchParams.keys()).some((key) => !nonSaveable.includes(key));
    }, [searchParams, ignoreParams]);

    // Canonical form of the current URL, used to highlight a matching saved view.
    const currentQuery = React.useMemo(
        () => canonicalQuery(searchParams.toString()),
        [searchParams]
    );

    // Whether the current URL already matches one of the saved filters — if so,
    // it's already saved, so we don't offer to save it again.
    const matchesSavedFilter = React.useMemo(
        () => saved.some((filter) => canonicalQuery(filter.query) === currentQuery),
        [saved, currentQuery]
    );

    React.useEffect(() => {
        if (isOutsideClick) {
            setIsOpen(false);
        }
    }, [isOutsideClick]);

    const handleSave = React.useCallback(() => {
        const trimmed = name.trim();
        if (!trimmed) {
            return;
        }
        const params = new URLSearchParams(searchParams);
        PAGINATION_KEYS.forEach((key) => params.delete(key));
        setSaved((prev) => [
            ...prev,
            { id: `${Date.now()}`, name: trimmed, query: params.toString() }
        ]);
        setName('');
        setIsOpen(false);
    }, [name, searchParams, setSaved]);

    const handleApply = React.useCallback(
        (query: string) => setSearchParams(query),
        [setSearchParams]
    );

    const handleDelete = React.useCallback(
        (id: string) => setSaved((prev) => prev.filter((filter) => filter.id !== id)),
        [setSaved]
    );

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                handleSave();
            }
        },
        [handleSave]
    );

    const hasContent = saved.length > 0 || hasSavableQuery;

    return (
        <div className={Style.wrapper}>
            {showLeadingDivider && hasContent && <span className={Style.divider} />}

            {saved.map((filter) => (
                <div key={filter.id} className={Style.tabItem}>
                    <button
                        type="button"
                        className={`${Style.tab} ${
                            canonicalQuery(filter.query) === currentQuery ? Style.active : ''
                        }`}
                        aria-label={`Apply ${filter.name}`}
                        onClick={() => handleApply(filter.query)}
                    >
                        {filter.name}
                    </button>
                    <button
                        type="button"
                        className={Style.del}
                        aria-label={`Delete ${filter.name}`}
                        onClick={() => handleDelete(filter.id)}
                    >
                        <XIcon size="utility" />
                    </button>
                </div>
            ))}

            {hasSavableQuery && !matchesSavedFilter && (
                <div className={Style.saveWrapper} ref={ref as React.RefObject<HTMLDivElement>}>
                    <button
                        type="button"
                        aria-label="Save current filter"
                        className={Style.save}
                        onClick={() => setIsOpen((open) => !open)}
                    >
                        <PlusIcon size="utility" />
                        <span>Save current filter</span>
                    </button>

                    {isOpen && (
                        <div className={Style.dropdown}>
                            <div className={Style.saveRow}>
                                <input
                                    className={Style.nameInput}
                                    placeholder="Filter name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    autoFocus
                                />
                                <Button
                                    variant="primary"
                                    size="small"
                                    noDefaultMargin
                                    disabled={!name.trim()}
                                    onClick={handleSave}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SavedFilters;
